import type { ScreenContext } from "@/types";

/** Keep the final merged snapshot within the conversation API's field limits. */
export function boundScreenContext(source: ScreenContext): ScreenContext {
  let truncated = !!source.truncated;
  const text = (value: string, max: number) => {
    if (value.length > max) truncated = true;
    return value.slice(0, max);
  };
  const list = <T>(items: T[], max: number): T[] => {
    if (items.length > max) truncated = true;
    return items.slice(0, max);
  };
  const context: ScreenContext = {
    ...source,
    elements: list(source.elements, 50).map((v) => text(v, 120)),
    formSections:
      source.formSections &&
      list(source.formSections, 80).map((section) => ({
        ...section,
        id: text(section.id, 120),
        label: text(section.label, 200),
      })),
    activeFormSection:
      source.activeFormSection && text(source.activeFormSection, 120),
    focusedField: source.focusedField && text(source.focusedField, 120),
    activeView:
      source.activeView === undefined
        ? undefined
        : text(source.activeView, 100),
    pageTitle:
      source.pageTitle === undefined ? undefined : text(source.pageTitle, 200),
    summary:
      source.summary === undefined ? undefined : text(source.summary, 3000),
    keyMetrics:
      source.keyMetrics &&
      Object.fromEntries(
        Object.entries(source.keyMetrics).map(([k, v]) => [
          text(k, 120),
          typeof v === "string" ? text(v, 500) : v,
        ]),
      ),
    visibleRecords:
      source.visibleRecords &&
      list(source.visibleRecords, 30).map((record) => ({
        ...record,
        table: text(record.table, 120),
        fields: Object.fromEntries(
          Object.entries(record.fields).map(([k, v]) => [
            text(k, 120),
            text(v, 500),
          ]),
        ),
      })),
    chartSummaries:
      source.chartSummaries &&
      list(source.chartSummaries, 20).map((v) => text(v, 1500)),
    formFields:
      source.formFields &&
      list(source.formFields, 40).map((field) => ({
        ...field,
        label: text(field.label, 120),
        control: text(field.control, 40),
        value: field.value === undefined ? undefined : text(field.value, 500),
        error: field.error === undefined ? undefined : text(field.error, 500),
        options:
          field.options && list(field.options, 20).map((v) => text(v, 120)),
      })),
  };
  context.truncated = truncated || undefined;
  // Use UTF-8 bytes, leaving room below the server's 18,000-character limit.
  while (new TextEncoder().encode(JSON.stringify(context)).length > 16_000) {
    context.truncated = true;
    if (context.formSections && context.formSections.length > 8) {
      const index = context.formSections.findLastIndex(
        (section) => section.id !== context.activeFormSection,
      );
      context.formSections.splice(index, 1);
    } else if (context.visibleRecords?.length) context.visibleRecords.pop();
    else if (context.formFields?.length) context.formFields.pop();
    else if (context.chartSummaries?.length) context.chartSummaries.pop();
    else if (context.keyMetrics && Object.keys(context.keyMetrics).length) {
      delete context.keyMetrics[Object.keys(context.keyMetrics).at(-1)!];
    } else if (context.summary)
      context.summary = context.summary.slice(
        0,
        Math.floor(context.summary.length / 2),
      );
    else if (context.elements.length) context.elements.pop();
    else if (context.formSections?.length) context.formSections.pop();
    else break;
  }
  return context;
}
